import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Slider } from '../../components/ui/slider';
import {
  CheckCircle2, ChevronLeft, ChevronRight, Loader2, Sparkles,
  Target, Eye, Wand2, Layers, Lightbulb, Compass, Trophy, Heart,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { logger } from '../../lib/logger';

/**
 * HandoffReportPage — public, tokenised 8-section "passaggio di consegna"
 * form. Visited by the Associate via the link they receive in their
 * Telegram DM right after picking the Senior with whom they fixed the
 * Zoom call. Submit:
 *   • Emails morena@housecashflows.com with the rich 8-section report.
 *   • DMs the chosen Senior with the digest.
 *   • DMs the Associate as confirmation.
 *
 * The same form/UI is shared with `components/ZoomReportFormModal.jsx`
 * (in-CRM version for the Senior post-Zoom). This page makes the form
 * available without requiring a CRM login — the HMAC token in the URL
 * is the auth.
 */

const PROFILE_OPTIONS = [
  { value: 'LIFE',   label: 'LIFE · Lifestyle buyer' },
  { value: 'TIME',   label: 'TIME · Opportunista strategico' },
  { value: 'SAFE',   label: 'SAFE · Protettivo patrimoniale' },
  { value: 'CASH',   label: 'CASH · Cashflow buyer' },
  { value: 'FAME',   label: 'FAME · Status silenzioso' },
  { value: 'VISION', label: 'VISION · Visionario' },
  { value: 'DEEP',   label: 'DEEP · Analitico' },
  { value: 'HOME',   label: 'HOME · Family buyer' },
];
const PATH_OPTIONS = [
  { value: 'equity_share', label: 'Equity Share' },
  { value: 'real_estate',  label: 'Real Estate (proprietà piena)' },
  { value: 'both',         label: 'Entrambi' },
];
const RESORT_OPTIONS = [
  { value: 'lombok', label: '🇮🇩 Lombok (Indonesia)' },
  { value: 'spille', label: '🇦🇱 Spille (Albania)' },
  { value: 'phuket', label: '🇹🇭 Phuket (Thailandia)' },
  { value: 'multi',  label: '🌍 Multi-resort' },
];
const REACTION_OPTIONS = [
  { value: 'entusiasta',  label: '🤩 Entusiasta' },
  { value: 'interessato', label: '👍 Interessato' },
  { value: 'tiepido',     label: '😐 Tiepido' },
  { value: 'scettico',    label: '🤨 Scettico' },
];
const DIFFERENTIATORS = [
  'governance_HCF', 'asset_reale', 'community_soci', 'brand_internazionale',
  'tax_engine', 'mercato_secondario', 'tre_destinazioni',
];
const DIFF_LABELS = {
  governance_HCF:       'Governance HCF',
  asset_reale:          'Asset reale (resort)',
  community_soci:       'Community soci',
  brand_internazionale: 'Brand internazionale',
  tax_engine:           'Tax engine multi-paese',
  mercato_secondario:   'Mercato secondario',
  tre_destinazioni:     '3 destinazioni · 1 quota',
};
const OUTCOME_OPTIONS = [
  { value: 'procede',         label: '🟢 PROCEDE',         desc: 'Già pagato o firmato' },
  { value: 'interessato',     label: '🟡 INTERESSATO',     desc: 'Zoom fissata · pronto al passaggio' },
  { value: 'riflessione',     label: '🟠 RIFLESSIONE',     desc: 'Vuole più tempo' },
  { value: 'non_interessato', label: '🔴 NON INTERESSATO', desc: 'Da chiudere' },
];
const SECTIONS = [
  { idx: 1, title: 'Entrata e continuità',          time: '0-3 min',   icon: Heart },
  { idx: 2, title: 'Conferma profilo cliente',       time: '3-7 min',   icon: Target },
  { idx: 3, title: 'Lettura strategica · specchio',  time: '7-11 min',  icon: Eye },
  { idx: 4, title: 'Costruzione visione',            time: '11-16 min', icon: Wand2 },
  { idx: 5, title: 'Solution + convenience',         time: '16-22 min', icon: Lightbulb },
  { idx: 6, title: 'Experience HCF',                 time: '22-24 min', icon: Layers },
  { idx: 7, title: 'Proposta reale',                 time: '24-28 min', icon: Compass },
  { idx: 8, title: 'Chiusura e Zoom fissata',        time: '28-30 min', icon: Trophy },
];

export default function HandoffReportPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [meta, setMeta] = useState(null); // { lead, associate, senior, already_submitted }
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [structured, setStructured] = useState(() => initialPayload());

  // ── Bootstrap: validate the token + load context ─────────────────
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await api.get(`/public/handoff-report/${token}`);
        setMeta(r.data);
        if (r.data?.already_submitted) setDone(true);
      } catch (err) {
        const msg = err?.response?.data?.detail || 'Link non valido o scaduto.';
        setError(typeof msg === 'string' ? msg : 'Errore nel caricamento del modulo.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const setField = useCallback((k, v) => setStructured((p) => ({ ...p, [k]: v })), []);
  const toggleDiff = useCallback((d) => setStructured((p) => ({
    ...p,
    hcf_differentiators: p.hcf_differentiators.includes(d)
      ? p.hcf_differentiators.filter((x) => x !== d)
      : [...p.hcf_differentiators, d],
  })), []);

  const handleSubmit = useCallback(async () => {
    if (!structured.outcome) {
      toast.error('Seleziona un esito nella sezione 8 prima di inviare.');
      setStep(7);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        structured: {
          ...structured,
          alignment_score: Number(structured.alignment_score) || null,
          lead_temperature_final: Number(structured.lead_temperature_final) || null,
          units_count: structured.units_count ? Number(structured.units_count) : null,
          amount_proposed: structured.amount_proposed ? Number(structured.amount_proposed) : null,
          amount_confirmed: structured.amount_confirmed ? Number(structured.amount_confirmed) : null,
        },
      };
      await api.post(`/public/handoff-report/${token}`, payload);
      setDone(true);
      toast.success('Report inviato! Morena e il Senior sono stati avvisati.');
    } catch (err) {
      logger.error('handoff-report submit failed', err);
      const detail = err?.response?.data?.detail || 'Errore invio report';
      toast.error(typeof detail === 'string' ? detail : 'Errore invio report');
    } finally {
      setSubmitting(false);
    }
  }, [structured, token]);

  const progressPct = useMemo(() => Math.round(((step + 1) / SECTIONS.length) * 100), [step]);
  const SectionIcon = SECTIONS[step].icon;

  // ── Render branches ──────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-muted-foreground" data-testid="handoff-loading">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center px-4" data-testid="handoff-error">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h1 className="font-serif text-xl text-[hsl(var(--primary))] mb-2">Link non disponibile</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
          <p className="text-xs text-muted-foreground mt-4">
            Se hai bisogno di un nuovo link, chiedi al bot Telegram di rigenerare il passaggio.
          </p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-[var(--gp-forest-dark,#082019)] text-white grid place-items-center px-4" data-testid="handoff-done">
        <div className="max-w-md text-center">
          <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
          <h1 className="font-serif text-2xl mb-2">Modulo inviato</h1>
          <p className="text-white/70 text-sm">
            Ho avvisato <b>{meta?.senior?.name || 'il Senior'}</b> e Morena. Buon lavoro!
          </p>
          <p className="text-white/40 text-xs mt-6">Puoi chiudere questa pagina.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--surface-1,#fbfaf6)]" data-testid="handoff-page">
      {/* Top bar */}
      <header className="bg-[var(--gp-forest,#0F2A1B)] text-white">
        <div className="max-w-2xl mx-auto px-4 md:px-6 py-4">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--gp-gold-soft,#e6d4a8)] mb-1">
            Modulo di passaggio Associate → Senior
          </div>
          <h1 className="font-serif text-xl md:text-2xl">{meta?.lead?.name || 'Lead'}</h1>
          <div className="text-xs text-white/70 mt-1">
            <span>Associate: <b>{meta?.associate?.name || '—'}</b></span>
            <span className="mx-2">·</span>
            <span>Senior: <b>{meta?.senior?.name || '—'}</b></span>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 md:px-6 py-5 space-y-4">
        {/* Progress */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Sezione <b>{SECTIONS[step].idx}</b>/8 · <span className="text-[var(--gp-gold,#C9A34A)]">{SECTIONS[step].time}</span></span>
          <span>{progressPct}%</span>
        </div>
        <div className="h-1.5 bg-zinc-200 rounded-full overflow-hidden">
          <div className="h-full bg-[var(--gp-forest,#0F2A1B)] transition-all duration-300" style={{ width: `${progressPct}%` }} />
        </div>

        <div className="bg-white border border-zinc-200/60 rounded-lg p-4 shadow-sm" data-testid={`handoff-section-${SECTIONS[step].idx}`}>
          <div className="flex items-center gap-2 mb-3">
            <SectionIcon className="w-4 h-4 text-[var(--gp-forest,#0F2A1B)]" />
            <h3 className="font-serif text-lg text-[var(--gp-forest,#0F2A1B)]">{SECTIONS[step].title}</h3>
          </div>

          {step === 0 && <Section1 v={structured} set={setField} />}
          {step === 1 && <Section2 v={structured} set={setField} />}
          {step === 2 && <Section3 v={structured} set={setField} />}
          {step === 3 && <Section4 v={structured} set={setField} />}
          {step === 4 && <Section5 v={structured} set={setField} />}
          {step === 5 && <Section6 v={structured} set={setField} toggleDiff={toggleDiff} />}
          {step === 6 && <Section7 v={structured} set={setField} />}
          {step === 7 && <Section8 v={structured} set={setField} />}
        </div>

        {/* Nav */}
        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" disabled={step === 0 || submitting} onClick={() => setStep((s) => Math.max(0, s - 1))} data-testid="handoff-prev">
            <ChevronLeft className="w-4 h-4 mr-1" /> Indietro
          </Button>
          {step < SECTIONS.length - 1 ? (
            <Button onClick={() => setStep((s) => Math.min(SECTIONS.length - 1, s + 1))} data-testid="handoff-next" className="bg-[var(--gp-forest,#0F2A1B)] hover:bg-[var(--gp-forest-dark,#082019)] text-white">
              Avanti <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting || !structured.outcome} data-testid="handoff-submit" className="bg-emerald-700 hover:bg-emerald-800 text-white">
              {submitting ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Invio…</>) : (<><CheckCircle2 className="w-4 h-4 mr-1.5" /> Invia al Senior</>)}
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}

// ── State init ─────────────────────────────────────────────────────
function initialPayload() {
  return {
    trust_built: '', associate_notes_confirmed: '', section1_notes: '',
    profile_confirmed: '', profile_secondary: '', client_needs: '', client_goals: '',
    alignment_score: 7, mirror_phrase: '',
    vision_pitched: '', client_reaction: '',
    solution_proposed: '', objections: '',
    hcf_differentiators: [], hcf_client_response: '',
    path_chosen: '', resort_chosen: '', units_count: '', amount_proposed: '', concrete_interest: '',
    outcome: '', amount_confirmed: '', next_step_date: '', next_step_desc: '',
    lead_temperature_final: 6, notes_final: '',
  };
}

// ── Section components ─────────────────────────────────────────────
function YesNo({ value, onChange, testid }) {
  return (
    <RadioGroup value={value} onValueChange={onChange} className="flex gap-4 mt-1" data-testid={testid}>
      <label className="flex items-center gap-1.5 text-sm cursor-pointer"><RadioGroupItem value="si" id={`${testid}-si`} /> Sì</label>
      <label className="flex items-center gap-1.5 text-sm cursor-pointer"><RadioGroupItem value="parziale" id={`${testid}-p`} /> Parziale</label>
      <label className="flex items-center gap-1.5 text-sm cursor-pointer"><RadioGroupItem value="no" id={`${testid}-no`} /> No</label>
    </RadioGroup>
  );
}

function Section1({ v, set }) {
  return (
    <div className="space-y-3">
      <div><Label>Hai instaurato fiducia nei primi minuti?</Label><YesNo value={v.trust_built} onChange={(x) => set('trust_built', x)} testid="h-trust" /></div>
      <div><Label>Note sul lead confermate dalla chiamata?</Label><YesNo value={v.associate_notes_confirmed} onChange={(x) => set('associate_notes_confirmed', x)} testid="h-notes-conf" /></div>
      <div><Label htmlFor="h-s1n">Note libere</Label><Textarea id="h-s1n" rows={3} value={v.section1_notes} onChange={(e) => set('section1_notes', e.target.value)} placeholder="Es. lead disponibile, ha confermato età e provenienza…" /></div>
    </div>
  );
}

function Section2({ v, set }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label>Profilo dominante</Label>
          <Select value={v.profile_confirmed} onValueChange={(x) => set('profile_confirmed', x)}>
            <SelectTrigger data-testid="h-profile-primary"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
            <SelectContent>{PROFILE_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Profilo secondario</Label>
          <Select value={v.profile_secondary} onValueChange={(x) => set('profile_secondary', x)}>
            <SelectTrigger><SelectValue placeholder="Seleziona…" /></SelectTrigger>
            <SelectContent>{PROFILE_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div><Label htmlFor="h-needs">Bisogni emersi</Label><Textarea id="h-needs" rows={3} value={v.client_needs} onChange={(e) => set('client_needs', e.target.value)} /></div>
      <div><Label htmlFor="h-goals">Obiettivi a 12 mesi</Label><Textarea id="h-goals" rows={3} value={v.client_goals} onChange={(e) => set('client_goals', e.target.value)} /></div>
    </div>
  );
}

function Section3({ v, set }) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="flex items-center justify-between"><span>Allineamento percepito (1-10)</span><Badge variant="outline">{v.alignment_score}/10</Badge></Label>
        <Slider value={[v.alignment_score]} onValueChange={(x) => set('alignment_score', x[0])} min={1} max={10} step={1} className="mt-2" />
      </div>
      <div><Label htmlFor="h-mirror">Frase-specchio usata</Label><Textarea id="h-mirror" rows={3} value={v.mirror_phrase} onChange={(e) => set('mirror_phrase', e.target.value)} /></div>
    </div>
  );
}

function Section4({ v, set }) {
  return (
    <div className="space-y-3">
      <div><Label htmlFor="h-vision">Visione presentata</Label><Textarea id="h-vision" rows={4} value={v.vision_pitched} onChange={(e) => set('vision_pitched', e.target.value)} /></div>
      <div>
        <Label>Reazione del cliente</Label>
        <Select value={v.client_reaction} onValueChange={(x) => set('client_reaction', x)}>
          <SelectTrigger data-testid="h-reaction"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
          <SelectContent>{REACTION_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </div>
  );
}

function Section5({ v, set }) {
  return (
    <div className="space-y-3">
      <div><Label htmlFor="h-sol">Soluzione proposta</Label><Textarea id="h-sol" rows={4} value={v.solution_proposed} onChange={(e) => set('solution_proposed', e.target.value)} /></div>
      <div><Label htmlFor="h-obj">Obiezioni emerse</Label><Textarea id="h-obj" rows={3} value={v.objections} onChange={(e) => set('objections', e.target.value)} /></div>
    </div>
  );
}

function Section6({ v, toggleDiff, set }) {
  return (
    <div className="space-y-3">
      <Label>Punti di differenziazione mostrati</Label>
      <div className="grid grid-cols-2 gap-2">
        {DIFFERENTIATORS.map((d) => (
          <label key={d} className="flex items-center gap-2 text-sm cursor-pointer p-1.5 rounded hover:bg-zinc-100">
            <Checkbox checked={v.hcf_differentiators.includes(d)} onCheckedChange={() => toggleDiff(d)} data-testid={`h-diff-${d}`} />
            {DIFF_LABELS[d]}
          </label>
        ))}
      </div>
      <div><Label htmlFor="h-resp">Risposta del cliente</Label><Textarea id="h-resp" rows={3} value={v.hcf_client_response} onChange={(e) => set('hcf_client_response', e.target.value)} /></div>
    </div>
  );
}

function Section7({ v, set }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label>Percorso</Label>
          <Select value={v.path_chosen} onValueChange={(x) => set('path_chosen', x)}>
            <SelectTrigger data-testid="h-path"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
            <SelectContent>{PATH_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Resort</Label>
          <Select value={v.resort_chosen} onValueChange={(x) => set('resort_chosen', x)}>
            <SelectTrigger data-testid="h-resort"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
            <SelectContent>{RESORT_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label htmlFor="h-units">Quote/unità</Label><Input id="h-units" type="number" min={0} value={v.units_count} onChange={(e) => set('units_count', e.target.value)} /></div>
        <div><Label htmlFor="h-amt">Importo (EUR)</Label><Input id="h-amt" type="number" min={0} value={v.amount_proposed} onChange={(e) => set('amount_proposed', e.target.value)} data-testid="h-amount" /></div>
      </div>
      <div><Label>Interesse concreto?</Label><YesNo value={v.concrete_interest} onChange={(x) => set('concrete_interest', x)} testid="h-interest" /></div>
    </div>
  );
}

function Section8({ v, set }) {
  return (
    <div className="space-y-3">
      <div>
        <Label>Stato attuale del lead *</Label>
        <RadioGroup value={v.outcome} onValueChange={(x) => set('outcome', x)} className="space-y-1.5 mt-2" data-testid="h-outcome">
          {OUTCOME_OPTIONS.map((o) => (
            <label key={o.value} className="flex items-start gap-3 cursor-pointer p-2 rounded-md hover:bg-zinc-100 border border-zinc-200/40">
              <RadioGroupItem value={o.value} id={`o-${o.value}`} className="mt-0.5" />
              <div><div className="text-sm font-medium">{o.label}</div><div className="text-xs text-muted-foreground">{o.desc}</div></div>
            </label>
          ))}
        </RadioGroup>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label htmlFor="h-next-date">Data Zoom fissata</Label><Input id="h-next-date" type="date" value={v.next_step_date} onChange={(e) => set('next_step_date', e.target.value)} /></div>
        <div><Label htmlFor="h-next-desc">Orario/note Zoom</Label><Input id="h-next-desc" value={v.next_step_desc} onChange={(e) => set('next_step_desc', e.target.value)} placeholder="Es. venerdì 17:00" /></div>
      </div>
      <div className="pt-2 border-t border-zinc-200/60">
        <Label className="flex items-center justify-between"><span>Temperatura finale (1-10)</span><Badge variant="outline">{v.lead_temperature_final}/10</Badge></Label>
        <Slider value={[v.lead_temperature_final]} onValueChange={(x) => set('lead_temperature_final', x[0])} min={1} max={10} step={1} className="mt-2" />
      </div>
      <div><Label htmlFor="h-final">Note finali per il Senior</Label><Textarea id="h-final" rows={3} value={v.notes_final} onChange={(e) => set('notes_final', e.target.value)} placeholder="Cosa deve sapere assolutamente il Senior prima della Zoom?" /></div>
    </div>
  );
}
